import { useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import Badge from '../../ui/Badge';
import { formatMoney, formatDate } from '../../../utils/formatters';
import { confirmMatch, rejectMatch } from '../../../api/mec';
import { labelFor as methodLabelImpl } from '../mecLabels';

// Single row for a Tier 2 / pending-review match. Used in the location
// drill-down and the global Tier 2 review queue. Confirm / Reject mutate
// the row in place; on success the parent updates its local list via
// onResolved.

// D-037 (2026-05-29): credit-pending family + Tier 3 reclassification.
// Both new and legacy method names are recognized here so historical
// mec_match rows (committed before D-037) still render correctly.
// D-039: format QBO doc number prefix per transaction type.
// Bill → "Bill#", Check → "CH#", VendorCredit → "Credit#",
// anything else (Purchase / CreditCardPayment / unknown) → "Doc#".
export function qboDocLabel(txnType) {
  switch (txnType) {
    case 'Bill': return 'Bill#';
    case 'Check': return 'CH#';
    case 'VendorCredit': return 'Credit#';
    default: return 'Doc#';
  }
}

export function methodBadgeTone(method) {
  switch (method) {
    case 'tier_1_po': return 'recovery';
    case 'tier_2_heuristic': return 'action';

    // Credit-pending family — all insight tone (awaiting credit, not lost).
    case 'credit_pending_po_unused':
    case 'credit_pending_zero_cost':
    case 'warranty_credit_pending':
    case 'credit_pending_manual':
    // D-038: auto-flipped case-c (ordered-not-received) where QBO matched
    // by amount + date within ±2 days of order_date. Routes to A_pending_return.
    case 'credit_pending_unreceived_auto_matched':
    // D-041: cores outstanding — deposit paid, awaiting vendor credit memo.
    case 'core_pending_return':
    // Legacy names — kept rendering same as their D-037 equivalents.
    case 'pending_credit_warranty_tagged':
    case 'pending_credit_zero_cost_match':
    case 'pending_credit_manual':
      return 'insight';

    // tier_3_po_unused moved out of Tier 3 to credit_pending_po_unused under
    // D-037, but legacy rows still carry the old name — render those as
    // insight too (they're awaiting credit, not leakage).
    case 'tier_3_po_unused':
      return 'insight';

    case 'tier_3_po_not_in_tekmetric':
    case 'tier_3_no_po':
      return 'danger';
    case 'ambiguous':
      return 'action';
    case 'return_bucket_pending':
      return 'cream';
    default: return 'neutral';
  }
}

// Re-export so existing callers (`import { methodLabel } from '.../MatchRow'`)
// don't break. The canonical label map lives in `../mecLabels.js`.
export function methodLabel(method) {
  return methodLabelImpl(method);
}

// Method-name sets for filtering UI tabs. New names and legacy names share
// the same operational meaning, so any UI filter that surfaces "credit
// pending" rows must match both.
export const CREDIT_PENDING_PO_UNUSED_METHODS = [
  'credit_pending_po_unused', 'tier_3_po_unused',
];
export const CREDIT_PENDING_ZERO_COST_METHODS = [
  'credit_pending_zero_cost', 'pending_credit_zero_cost_match',
];
export const WARRANTY_CREDIT_PENDING_METHODS = [
  'warranty_credit_pending', 'pending_credit_warranty_tagged',
];
export const CREDIT_PENDING_MANUAL_METHODS = [
  'credit_pending_manual', 'pending_credit_manual',
];
export const CREDIT_PENDING_AUTO_MATCHED_METHODS = [
  'credit_pending_unreceived_auto_matched',
];
// D-041 (2026-06-22): cores outstanding — visibility-only rows for vendor
// core deposits we paid upfront and are waiting on refund credit memos for.
export const CORES_OUTSTANDING_METHODS = [
  'core_pending_return',
];
export const ALL_CREDIT_PENDING_METHODS = [
  ...CREDIT_PENDING_PO_UNUSED_METHODS,
  ...CREDIT_PENDING_ZERO_COST_METHODS,
  ...WARRANTY_CREDIT_PENDING_METHODS,
  ...CREDIT_PENDING_MANUAL_METHODS,
  ...CREDIT_PENDING_AUTO_MATCHED_METHODS,
  ...CORES_OUTSTANDING_METHODS,
];

export default function MatchRow({
  match,
  onResolved,
  showCheckbox = false,
  checked = false,
  onCheck = null,
  compact = false,
}) {
  const [busy, setBusy] = useState(null); // 'confirm' | 'reject' | null
  const [error, setError] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const doConfirm = async () => {
    setBusy('confirm');
    setError(null);
    try {
      const updated = await confirmMatch(match.id);
      onResolved?.(match.id, { ...match, ...updated, review_status: 'confirmed' });
    } catch (err) {
      setError(err?.message || 'Failed to confirm');
    } finally {
      setBusy(null);
    }
  };

  const doReject = async () => {
    setBusy('reject');
    setError(null);
    try {
      const updated = await rejectMatch(match.id, rejectReason);
      onResolved?.(match.id, { ...match, ...updated, review_status: 'rejected' });
    } catch (err) {
      setError(err?.message || 'Failed to reject');
    } finally {
      setBusy(null);
    }
  };

  const isResolved = match.review_status === 'confirmed' || match.review_status === 'rejected';
  const dAmount = match.amount_delta_cents != null ? match.amount_delta_cents / 100 : null;
  const dDays = match.date_delta_days;
  const score = match.score;

  return (
    <tr className={`
      border-b border-[var(--color-warm-100)] last:border-b-0
      ${isResolved ? 'bg-[var(--color-cream-100)]/50 opacity-70' : 'hover:bg-[var(--color-cream-100)]/40'}
      transition-colors
    `}>
      {showCheckbox && (
        <td className="px-3 py-3 align-middle">
          <input
            type="checkbox"
            checked={checked}
            disabled={isResolved}
            onChange={(e) => onCheck?.(match.id, e.target.checked)}
            className="accent-[var(--color-warm-900)] w-4 h-4 cursor-pointer disabled:opacity-30"
          />
        </td>
      )}

      <td className="px-3 py-3 align-top min-w-[200px]">
        {match._shop_name && (
          <div className="inline-flex items-center gap-1 mb-1">
            <Badge tone="cream" size="xs">{match._shop_name}</Badge>
          </div>
        )}
        <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">
          {match.qbo_vendor || '— vendor —'}
        </div>
        <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5">
          {formatDate(match.qbo_date)}
          {match.qbo_txn_id && (
            <span className="mono-nums"> · txn {match.qbo_txn_id.slice(-8)}</span>
          )}
          {match.qbo_doc_number && (
            <span className="mono-nums"> · {qboDocLabel(match.qbo_txn_type)}{match.qbo_doc_number}</span>
          )}
        </div>
        {match.qbo_memo && !compact && (
          <div className="text-[11px] text-[var(--color-warm-600)] mt-1 italic line-clamp-2 max-w-[280px]">
            {match.qbo_memo}
          </div>
        )}
      </td>

      <td className="px-3 py-3 align-middle text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
        {formatMoney((match.qbo_amount_cents || 0) / 100)}
      </td>

      <td className="px-3 py-3 align-top min-w-[180px]">
        {match.matched_po_number ? (
          <>
            <div className="text-[12px] font-semibold text-[var(--color-warm-800)] mono-nums">
              PO {match.matched_po_number}
            </div>
            {(match.matched_ro_number || match.matched_ro_id) && (
              <div className="text-[11px] text-[var(--color-warm-500)] mono-nums mt-0.5">
                RO #{match.matched_ro_number || match.matched_ro_id}
              </div>
            )}
            {match.matched_date && (
              <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5">
                {formatDate(match.matched_date)}
              </div>
            )}
            {match.matched_amount_cents != null && (
              <div className="text-[11px] text-[var(--color-warm-600)] tabular-nums mt-0.5">
                Tekmetric {formatMoney(match.matched_amount_cents / 100)}
              </div>
            )}
          </>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-500)] italic">no Tekmetric match</span>
        )}
      </td>

      <td className="px-3 py-3 align-middle text-right tabular-nums">
        {dAmount != null ? (
          <span className={`text-[12px] font-semibold ${
            Math.abs(dAmount) > 5 ? 'text-[var(--color-action-700)]' : 'text-[var(--color-warm-600)]'
          }`}>
            {dAmount > 0 ? '+' : ''}{formatMoney(dAmount)}
          </span>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-400)]">—</span>
        )}
      </td>

      <td className="px-3 py-3 align-middle text-right tabular-nums">
        {dDays != null ? (
          <span className={`text-[12px] ${
            Math.abs(dDays) > 7 ? 'text-[var(--color-action-700)]' : 'text-[var(--color-warm-600)]'
          }`}>
            {dDays > 0 ? '+' : ''}{dDays}d
          </span>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-400)]">—</span>
        )}
      </td>

      <td className="px-3 py-3 align-middle text-right tabular-nums">
        {score != null ? (
          <Badge tone={score >= 0.95 ? 'recovery' : score >= 0.80 ? 'action' : 'danger'} size="xs">
            {score.toFixed(2)}
          </Badge>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-400)]">—</span>
        )}
      </td>

      <td className="px-3 py-3 align-middle">
        <Badge tone={methodBadgeTone(match.matched_method)} size="xs">
          {methodLabel(match.matched_method)}
        </Badge>
      </td>

      <td className="px-3 py-3 align-middle text-right">
        {isResolved ? (
          <Badge
            tone={match.review_status === 'confirmed' ? 'success' : 'neutral'}
            size="xs"
            icon={match.review_status === 'confirmed' ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
          >
            {match.review_status}
          </Badge>
        ) : match.review_status === 'pending_review' ? (
          <div className="flex items-center justify-end gap-1.5">
            {showRejectInput ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  placeholder="reason (optional)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="h-7 px-2 text-[11px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-warm-400)] w-32"
                />
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={doReject}
                  className="h-7 px-2 text-[11px] font-semibold rounded-[var(--radius-sm)] bg-[var(--color-danger-500)] text-white hover:bg-[var(--color-danger-600)] disabled:opacity-50"
                >
                  {busy === 'reject' ? <Loader2 size={11} className="animate-spin" /> : 'Confirm reject'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                  className="h-7 px-2 text-[11px] text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]"
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={doConfirm}
                  className="h-7 px-2.5 text-[11px] font-semibold rounded-[var(--radius-sm)] bg-[var(--color-recovery-500)] text-white hover:bg-[var(--color-recovery-600)] disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {busy === 'confirm' ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Confirm
                </button>
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={() => setShowRejectInput(true)}
                  className="h-7 px-2.5 text-[11px] font-semibold rounded-[var(--radius-sm)] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] text-[var(--color-warm-700)] hover:bg-[var(--color-cream-200)] disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <XCircle size={11} />
                  Reject
                </button>
              </>
            )}
            {error && (
              <span className="inline-flex items-center gap-1 text-[10px] text-[var(--color-danger-600)] ml-1">
                <AlertCircle size={10} /> {error}
              </span>
            )}
          </div>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-500)]">{match.review_status || 'auto'}</span>
        )}
      </td>
    </tr>
  );
}
